import React, { useState } from "react";
import { Reveal } from "../components/Reveal";
import { Phone, Mail, MapPin, User, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useLang } from "../contexts/LanguageContext";
import SEO, { organizationSchema } from "../components/SEO";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error(t("contact_page.form_error_required"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact_page.form_success"));
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <SEO metaKey="contact" pathname="/contact" schemas={[organizationSchema()]} />

      <section className="relative isolate bg-forest text-white">
        <div className="absolute inset-0 z-0">
          <img src="/images/gallery/lsp-22.jpg" alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/55 md:bg-forest/75" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-40 min-h-[55vh] flex flex-col justify-end">
          <Reveal>
            <div className="editorial-divider mb-6 max-w-xs" style={{ color: "#E3A839" }}>{t("contact_page.eyebrow")}</div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.96] max-w-5xl">
              {t("contact_page.h1a")} <br /> <span className="italic text-mustard">{t("contact_page.h1b")}</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-6">
              <div className="p-7 rounded-xl border border-dust bg-kraft/30">
                <div className="flex items-center gap-3 mb-3"><User className="w-5 h-5 text-terracotta" /><div className="text-[12px] tracking-[0.3em] uppercase text-clay">{t("contact_page.chairman_label")}</div></div>
                <div className="font-serif text-2xl text-forest">{t("contact_page.chairman_name")}</div>
              </div>
              <div className="p-7 rounded-xl border border-dust bg-kraft/30">
                <div className="flex items-center gap-3 mb-3"><Phone className="w-5 h-5 text-terracotta" /><div className="text-[12px] tracking-[0.3em] uppercase text-clay">{t("contact_page.phone_label")}</div></div>
                <a href="tel:+919938056120" className="font-serif text-2xl text-forest link-sweep" data-testid="contact-phone-link">+91 99380 56120</a>
              </div>
              <div className="p-7 rounded-xl border border-dust bg-kraft/30">
                <div className="flex items-center gap-3 mb-3"><Mail className="w-5 h-5 text-terracotta" /><div className="text-[12px] tracking-[0.3em] uppercase text-clay">{t("contact_page.email_label")}</div></div>
                <a href="mailto:lspnaturalfarming@gmail.com" className="font-serif text-xl text-forest link-sweep break-all" data-testid="contact-email-link">lspnaturalfarming@gmail.com</a>
              </div>
              <div className="p-7 rounded-xl border border-dust bg-kraft/30">
                <div className="flex items-center gap-3 mb-3"><MapPin className="w-5 h-5 text-terracotta" /><div className="text-[12px] tracking-[0.3em] uppercase text-clay">{t("contact_page.address_label")}</div></div>
                <div className="font-serif text-xl text-forest leading-snug whitespace-pre-line">{t("contact_page.address")}</div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="p-8 lg:p-10 rounded-xl border border-dust bg-forest text-ivory">
              <h2 className="font-serif text-3xl lg:text-4xl">{t("contact_page.form_title")}</h2>
              <p className="mt-3 text-ivory/75">{t("contact_page.form_sub")}</p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="c-name" className="text-ivory/80">{t("common.name")} *</Label>
                  <Input id="c-name" data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ivory/10 border-ivory/20 text-ivory placeholder:text-ivory/40 mt-2" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-phone" className="text-ivory/80">{t("common.phone")} *</Label>
                    <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-ivory/10 border-ivory/20 text-ivory placeholder:text-ivory/40 mt-2" />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="text-ivory/80">{t("common.email")}</Label>
                    <Input id="c-email" data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-ivory/10 border-ivory/20 text-ivory placeholder:text-ivory/40 mt-2" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-msg" className="text-ivory/80">{t("common.message")} *</Label>
                  <Textarea id="c-msg" data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="bg-ivory/10 border-ivory/20 text-ivory placeholder:text-ivory/40 mt-2" />
                </div>
                <button type="submit" data-testid="contact-submit-btn" disabled={loading} className="btn-pill bg-mustard text-forest hover:bg-mustard/90 disabled:opacity-60">
                  {loading ? t("common.sending") : <>{t("common.send")} <Send className="w-4 h-4" /></>}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-kraft/40 border-t border-dust">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16">
          <div className="rounded-xl overflow-hidden border border-dust h-[360px]">
            <iframe title="LSP location map" src="https://maps.google.com/maps?q=Niali%2C%20Cuttack%2C%20Odisha&t=&z=11&ie=UTF8&iwloc=&output=embed" className="w-full h-full border-0" loading="lazy" />
          </div>
        </div>
      </section>
    </div>
  );
}
